<script setup lang="ts">
import friendLogo from '@/assets/pngs/friend_logo.png'
import friendMobileLogo from '@/assets/pngs/friend_mobile_logo.png'
import hamburger from '@/assets/svgs/hamburger.svg'
import search from '@/assets/svgs/search.svg'
import { useIsMobile } from '@/composables/useMediaQuery'
import { useAuthStore } from '@/stores/auth'
import type { FilterOption } from '@/types/filter.types'
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import FullScreenMenu from '../FullScreenMenu.vue'
import FullScreenSearch from '../FullScreenSearch.vue'
import BaseFilter from './BaseFilter.vue'

// 로그인 상태를 관리하는 변수
const authStore = useAuthStore()

// 미디어 쿼리 상태를 관리하는 변수
const isMobile = useIsMobile()
const router = useRouter()

// 전체 화면 메뉴 상태
const isMenuOpen = ref(false)
const isSearchOpen = ref(false)

// 검색창이 열릴 때 body 스크롤 제어
watch(isSearchOpen, (newValue) => {
  if (newValue) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const toggleSearch = () => {
  isSearchOpen.value = !isSearchOpen.value
}

const pushLoginView = () => {
  router.push('/login')
}

const pushHomeView = () => {
  if (authStore.state.account?.loggedIn) {
    router.push('/dashboard')
  } else {
    router.push('/')
  }
}

const filterOptions: FilterOption[] = [
  { idx: 0, name: '마이페이지', data: 'my' },
  { idx: 1, name: '로그아웃', data: 'logout' },
]

const selectedFilter = ref(filterOptions[0])

const handleFilterSelect = async (option: FilterOption) => {
  switch (option.data) {
    case 'my':
      router.push('/my')
      break
    case 'logout':
      await authStore.logout()
      router.push('/')
      break
  }
}
</script>

<template>
  <div class="app-header" :class="{ 'logged-in-header': authStore.isAuthenticated }">
    <div class="leading-icons">
      <img
        class="menu-icon"
        :class="{ 'svg-black': !authStore.isAuthenticated, 'svg-white': authStore.isAuthenticated }"
        :src="hamburger"
        alt="menu"
        @click="toggleMenu"
      />
    </div>
    <div class="app-logo-container">
      <img class="app-logo" :src="friendLogo" alt="logo" @click="pushHomeView" />
      <img class="app-logo-mobile" :src="friendMobileLogo" alt="logo" @click="pushHomeView" />
    </div>
    <div class="action-icons">
      <!-- 로그인한 경우: 검색 아이콘과 사용자 아이콘 표시 -->
      <template v-if="authStore.isAuthenticated">
        <img
          class="search-icon svg-white cursor-pointer"
          :src="search"
          alt="search"
          @click="toggleSearch"
        />
        <BaseFilter
          v-if="!isMobile"
          :options="filterOptions"
          v-model:selectedOption="selectedFilter"
          :width="140"
          :showArrow="true"
          customTrigger
          menuAlign="end"
          @select="handleFilterSelect"
        >
          <template #trigger>
            <div class="user-icon-container">
              <img
                v-if="authStore.state.account?.profileUrl"
                :src="authStore.state.account?.profileUrl"
                class="profile-image"
                alt="profile"
              />
              <div v-else class="profile-image-placeholder"></div>
            </div>
          </template>
        </BaseFilter>
      </template>
      <!-- 로그인하지 않은 경우: 로그인/회원가입 텍스트 표시 -->
      <template v-else>
        <div class="auth-links">
          <span
            :class="isMobile ? 'body-sm' : 'body-md'"
            class="login-signup-link"
            @click="pushLoginView"
            >로그인/회원가입</span
          >
        </div>
      </template>
    </div>
  </div>

  <!-- 전체 화면 메뉴 -->
  <FullScreenMenu :isVisible="isMenuOpen" @close="isMenuOpen = false" />
  <FullScreenSearch :isVisible="isSearchOpen" @close="isSearchOpen = false" />
</template>

<style scoped>
.app-header {
  width: 100%;
  display: flex;
  background-color: var(--white);
  padding: 13px 20px;
  align-items: center;
}

.logged-in-header {
  background-color: var(--gray-900);
}

.leading-icons {
  width: 104px;
  display: flex;
  gap: 20px;
  align-items: center;
}

.menu-icon {
  width: 40px;
  height: 40px;
  color: var(--gray-900);
  cursor: pointer;
}

.search-icon {
  width: 32px;
  height: 32px;
}

.action-icons {
  width: 104px;
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: flex-end;
}

.app-logo-container {
  display: flex;
  flex: 1;
  margin: 0;
  padding: 0 16px;
  justify-content: center;
  align-items: center;
}

.app-logo {
  width: 191px;
  height: 74px;
  cursor: pointer;
}

.app-logo-mobile {
  display: none;
  cursor: pointer;
}

.user-icon-container {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background-color: #3f4147;
  cursor: pointer;
  overflow: hidden;
}

.profile-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-image-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(0deg, rgba(33, 33, 33, 0.5) 0%, rgba(33, 33, 33, 0.5) 100%), #8f98a8;
}

/* 로그인/회원가입 영역 스타일 */
.auth-links {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
}

.divider {
  color: #666;
}

@media (max-width: 1365px) {
  .app-header {
    padding: 8px 20px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  }

  .app-logo {
    display: none;
  }

  .app-logo-mobile {
    display: block;
    width: 60px;
    height: 32px;
  }

  .menu-icon {
    width: 24px;
    height: 24px;
  }

  .search-icon {
    width: 24px;
    height: 24px;
  }

  .user-icon-container {
    display: none;
  }

  /* 모바일에서 로그인/회원가입 영역 스타일 */
  .auth-links {
    font-size: 12px;
  }
}
</style>
